import { useState } from "react";
import { useSelector } from "react-redux";
import { HiOutlineSearch, HiOutlineDownload, HiOutlineUser, HiOutlineMail } from "react-icons/hi";
import { exportResponsesCSV, exportResponsesXLSX } from "../../redux/api/surveyAPI";
import Button from "../shared/Button";
import Card from "../shared/Card";
import Badge from "../shared/Badge";
import TextInput from "../shared/TextInput";

const SurveyResponses = ({ responses, survey }) => {
    const token = useSelector((state) => state.auth?.accessToken);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterStatus, setFilterStatus] = useState("all");
    const [exporting, setExporting] = useState(null);

    const filteredResponses = responses.filter((response) => {
        const matchesSearch =
            !searchTerm ||
            response.answers.some((answer) =>
                String(answer.answer).toLowerCase().includes(searchTerm.toLowerCase())
            ) ||
            response.respondentInfo?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            response.respondentInfo?.email?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = filterStatus === "all" || response.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const formatDate = (date) => {
        if (!date) return "N/A";
        return new Date(date).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatTime = (seconds) => {
        if (!seconds) return "N/A";
        const minutes = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return minutes > 0 ? `${minutes}m ${secs}s` : `${secs}s`;
    };

    const handleExport = async (format) => {
        setExporting(format);
        try {
            if (format === "csv") {
                await exportResponsesCSV(survey._id, survey.title, token);
            } else {
                await exportResponsesXLSX(survey._id, survey.title, token);
            }
        } catch (err) {
            console.error("Export failed", err);
        } finally {
            setExporting(null);
        }
    };

    const renderAnswer = (answer) => {
        if (Array.isArray(answer)) return answer.join(", ");
        if (typeof answer === "boolean") return answer ? "Yes" : "No";
        return String(answer);
    };

    const getStatusVariant = (status) => {
        switch (status) {
            case "completed":
                return "success";
            case "started":
                return "info";
            default:
                return "default";
        }
    };

    return (
        <div className="space-y-4">
            {/* Filters and Export */}
            <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex gap-3 flex-1 flex-wrap">
                    <div className="flex-1 max-w-md">
                        <TextInput
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search responses..."
                            leadingIcon={<HiOutlineSearch className="text-xl" />}
                        />
                    </div>

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="h-11 px-4 rounded-sm border border-hairline bg-canvas text-ink text-body-md focus:outline-none focus:border-info-border"
                    >
                        <option value="all">All Responses</option>
                        <option value="completed">Completed</option>
                        <option value="started">Started</option>
                        <option value="abandoned">Abandoned</option>
                    </select>
                </div>

                <div className="flex gap-2">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleExport("csv")}
                        disabled={responses.length === 0 || exporting !== null}
                        leadingIcon={<HiOutlineDownload />}
                    >
                        {exporting === "csv" ? "Exporting…" : "Export CSV"}
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleExport("xlsx")}
                        disabled={responses.length === 0 || exporting !== null}
                        leadingIcon={<HiOutlineDownload />}
                    >
                        {exporting === "xlsx" ? "Exporting…" : "Export XLSX"}
                    </Button>
                </div>
            </div>

            {/* Responses List */}
            {filteredResponses.length === 0 ? (
                <Card variant="surface-soft" className="text-center !p-12">
                    <p className="text-title-sm text-muted">
                        {responses.length === 0
                            ? "No responses yet"
                            : "No responses match your filters"}
                    </p>
                </Card>
            ) : (
                <div className="space-y-4">
                    {filteredResponses.map((response, index) => (
                        <Card key={response._id} variant="default">
                            {/* Response Header */}
                            <div className="flex justify-between items-start mb-4 pb-4 border-b border-hairline">
                                <div>
                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                        <h4 className="text-title-sm text-ink">
                                            Response #{filteredResponses.length - index}
                                        </h4>
                                        {response.respondentInfo?.isLoggedIn && (
                                            <Badge variant="info" size="sm">Logged In</Badge>
                                        )}
                                    </div>

                                    {(response.respondentInfo?.name || response.respondentInfo?.email) && (
                                        <div className="flex items-center gap-4 mb-2 text-body-md text-ink flex-wrap">
                                            {response.respondentInfo?.name && (
                                                <div className="flex items-center gap-1">
                                                    <HiOutlineUser className="text-base text-muted" />
                                                    <span className="font-medium">{response.respondentInfo.name}</span>
                                                </div>
                                            )}
                                            {response.respondentInfo?.email && (
                                                <div className="flex items-center gap-1">
                                                    <HiOutlineMail className="text-base text-muted" />
                                                    <span>{response.respondentInfo.email}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {response.memberId && !response.respondentInfo?.name && (
                                        <div className="flex items-center gap-1 mb-2 text-body-md text-ink">
                                            <HiOutlineUser className="text-base text-muted" />
                                            <span className="font-medium">
                                                {response.memberId.firstName} {response.memberId.lastName}
                                            </span>
                                            {response.memberId.email && (
                                                <>
                                                    <span className="mx-2 text-muted">•</span>
                                                    <HiOutlineMail className="text-base text-muted" />
                                                    <span>{response.memberId.email}</span>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex gap-4 text-body-md text-muted flex-wrap">
                                        <span>Completed: {formatDate(response.completedAt)}</span>
                                        {response.completionTime && (
                                            <>
                                                <span>•</span>
                                                <span>Time: {formatTime(response.completionTime)}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <Badge variant={getStatusVariant(response.status)}>
                                    {response.status.charAt(0).toUpperCase() + response.status.slice(1)}
                                </Badge>
                            </div>

                            {/* Answers */}
                            <div className="space-y-4">
                                {response.answers.map((answer, aIndex) => {
                                    const question = survey.questions.find(
                                        (q) => q._id.toString() === answer.questionId.toString()
                                    );
                                    if (!question) return null;

                                    return (
                                        <div key={aIndex}>
                                            <div className="text-body-md font-medium mb-1 text-muted">
                                                {question.questionText}
                                            </div>
                                            <div className="p-3 rounded-md border border-hairline bg-surface-soft text-ink">
                                                {renderAnswer(answer.answer)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {filteredResponses.length > 0 && (
                <div className="text-center text-body-md text-muted">
                    Showing {filteredResponses.length} of {responses.length} responses
                </div>
            )}
        </div>
    );
};

export default SurveyResponses;
